import { useEffect, useMemo, useRef, useState } from "react";
import { GridColumnVisibilityModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";

export interface TableConfig {
	page?: number;
	pageSize?: number;
	sortField?: string;
	sortDirection?: "asc" | "desc";
	columnVisibilityModel?: GridColumnVisibilityModel;
}

interface Props {
	config: TableConfig;
	setConfig: (config: Partial<TableConfig>) => void;
}

export const useServerTableController = ({ config, setConfig }: Props) => {
	// prevents hydration overwrite
	const didHydrateRef = useRef(false);

	// local pagination state (UI stability)
	const [stablePage, setStablePage] = useState(config.page ?? 0);
	const [stablePageSize, setStablePageSize] = useState(config.pageSize ?? 10);

	// sync config → local state
	useEffect(() => {
		if (!config) return;

		if (!didHydrateRef.current) {
			didHydrateRef.current = true;

			setStablePage(config.page ?? 0);
			setStablePageSize(config.pageSize ?? 10);
			return;
		}

		setStablePage(config.page ?? 0);
		setStablePageSize(config.pageSize ?? 10);
	}, [config]);

	// pagination
	const paginationModel: GridPaginationModel = useMemo(() => {
		return {
			page: stablePage,
			pageSize: stablePageSize,
		};
	}, [stablePage, stablePageSize]);

	const handlePaginationChange = (model: GridPaginationModel) => {
		setStablePage(model.page);
		setStablePageSize(model.pageSize);

		setConfig({
			page: model.page,
			pageSize: model.pageSize,
		});
	};

	// sorting
	const sortModel: GridSortModel = useMemo(() => {
		return config.sortField ? [{ field: config.sortField, sort: config.sortDirection }] : [];
	}, [config.sortField, config.sortDirection]);

	const handleSortModelChange = (model: GridSortModel) => {
		const first = model[0];

		setConfig({
			sortField: first?.field,
			sortDirection: first?.sort || undefined,
		});
	};

	// column visibility
	const columnVisibilityModel = config.columnVisibilityModel ?? {};

	const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
		setConfig({
			columnVisibilityModel: model,
		});
	};

	return {
		paginationModel,
		handlePaginationChange,

		sortModel,
		handleSortModelChange,

		columnVisibilityModel,
		handleColumnVisibilityChange,
	};
};
